import React, { Component as BaseComponent } from 'react';
import RubikCubie, { cubieEdgeLength, faceOffsetByName } from './Cube';
import {
    rotatePointAroundAxis,
    combineAxisAngleRotations,
    measureProjectedMoveGap,
    readPointerCoordinates
} from '../utilities/utilities';
import { getDiscreteCubeState } from '../utilities/cubeState';
import {
    analyzeDiscreteCubeState,
    invertMoveIndex,
    notationForMoveIndex,
    parseMoveSequence,
    solveDiscreteCubeState,
    validateDiscreteCubeState
} from '../utilities/cubeSolver';

const initialViewHorizontalDegrees = 120;
const initialViewVerticalDegrees = 0;
export const scrambleMessageType = 'SCRAMBLE_RUBIK_CUBE';
const scrambleMoveCount = 24;
const solverStatusMessageType = 'RUBIK_CUBE_SOLVER_STATUS';
const bodyFaceAxisByName = {
    U: [0, -1, 0],
    D: [0, 1, 0],
    F: [0, 0, 1],
    B: [0, 0, -1],
    R: [1, 0, 0],
    L: [-1, 0, 0]
};
const applicationFaceNameByAxisIndex = ['F', 'B', 'D', 'U', 'R', 'L'];
const moveAnimationStepDegrees = 15;
const moveAnimationDelayMilliseconds = 16;

const createSolvedCubieCoordinates = () => ([
    [0, 0, 0],
    [-cubieEdgeLength, 0, 0],
    [cubieEdgeLength, 0, 0],
    [0, -cubieEdgeLength, 0],
    [0, cubieEdgeLength, 0],
    [-cubieEdgeLength, -cubieEdgeLength, 0],
    [-cubieEdgeLength, cubieEdgeLength, 0],
    [cubieEdgeLength, -cubieEdgeLength, 0],
    [cubieEdgeLength, cubieEdgeLength, 0],

    [0, 0, -cubieEdgeLength],
    [-cubieEdgeLength, 0, -cubieEdgeLength],
    [cubieEdgeLength, 0, -cubieEdgeLength],
    [0, -cubieEdgeLength, -cubieEdgeLength],
    [0, cubieEdgeLength, -cubieEdgeLength],
    [-cubieEdgeLength, -cubieEdgeLength, -cubieEdgeLength],
    [-cubieEdgeLength, cubieEdgeLength, -cubieEdgeLength],
    [cubieEdgeLength, -cubieEdgeLength, -cubieEdgeLength],
    [cubieEdgeLength, cubieEdgeLength, -cubieEdgeLength],

    [0, 0, cubieEdgeLength],
    [-cubieEdgeLength, 0, cubieEdgeLength],
    [cubieEdgeLength, 0, cubieEdgeLength],
    [0, -cubieEdgeLength, cubieEdgeLength],
    [0, cubieEdgeLength, cubieEdgeLength],
    [-cubieEdgeLength, -cubieEdgeLength, cubieEdgeLength],
    [-cubieEdgeLength, cubieEdgeLength, cubieEdgeLength],
    [cubieEdgeLength, -cubieEdgeLength, cubieEdgeLength],
    [cubieEdgeLength, cubieEdgeLength, cubieEdgeLength]
]);

const createSolvedCubeState = () => ({
    cubieCoordinates: createSolvedCubieCoordinates(),
    cubieRotationDegrees: Array(27).fill(0),
    cubieRotationAxes: Array(27).fill(0).map(() => [1, 0, 0]),
    activeLayerDegrees: 0,
    isViewDragActive: false,
    previousPointerPoint: {},
    selectedFaceName: undefined,
    selectedCubieIndex: undefined,
    activeLayerAxisIndex: null,
    isSnapRotationActive: undefined,
    snapStepDegrees: undefined,
    isReverseLayerTurn: false
});

const createInitialSolverStatus = () => ({
    mode: 'idle',
    message: 'Ready. Scramble the cube or turn any layer.',
    progressPercent: 0,
    solutionNotation: [],
    currentMoveIndex: 0,
    totalMoves: 0,
    movesRemaining: 0,
    currentMove: '',
    nodesVisited: 0,
    searchMilliseconds: 0,
    phaseOneDepth: 0,
    phaseTwoDepth: 0,
    metrics: null,
    validation: null
});

const normalizeRotationAxis = (rotationAxis) => {
    const axisLength = Math.sqrt(
        rotationAxis[0] * rotationAxis[0] +
        rotationAxis[1] * rotationAxis[1] +
        rotationAxis[2] * rotationAxis[2]
    );

    if (!isFinite(axisLength) || axisLength < .000001) {
        return [1, 0, 0];
    }

    return [
        rotationAxis[0] / axisLength,
        rotationAxis[1] / axisLength,
        rotationAxis[2] / axisLength
    ];
};

class RubikCubeWorkspace extends BaseComponent {

    constructor(componentProperties) {
        super(componentProperties);

        this.legacyRotationFlag = 0;
        this.pendingSnapTimerIdentifier = null;
        this.isScrambleRequestActive = false;
        this.isProgrammaticMoveActive = false;
        this.isSolverPaused = false;
        this.pendingMoveTimerIdentifier = null;
        this.solutionMoves = [];
        this.solutionMoveIndex = 0;
        this.solutionMustEndSolved = false;
        this.moveHistory = [];
        this.solverStatus = createInitialSolverStatus();
        this.state = createSolvedCubeState();

        this.readCubieOrientation = this.readCubieOrientation.bind(this);
        this.beginViewDrag = this.beginViewDrag.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.finishPointerAction = this.finishPointerAction.bind(this);
        this.rotateSelectedLayer = this.rotateSelectedLayer.bind(this);
        this.snapLayerToQuarterTurn = this.snapLayerToQuarterTurn.bind(this);
        this.rotateWholeCube = this.rotateWholeCube.bind(this);
        this.beginLayerTurn = this.beginLayerTurn.bind(this);
        this.scrambleCube = this.scrambleCube.bind(this);
        this.handleWindowMessage = this.handleWindowMessage.bind(this);
        this.getDiscreteState = this.getDiscreteState.bind(this);
        this.getSolverStatus = this.getSolverStatus.bind(this);
        this.solveCube = this.solveCube.bind(this);
        this.pauseSolver = this.pauseSolver.bind(this);
        this.continueSolver = this.continueSolver.bind(this);
        this.stepSolver = this.stepSolver.bind(this);
        this.undoLastMove = this.undoLastMove.bind(this);
        this.executeMoveSequence = this.executeMoveSequence.bind(this);
        this.validateCurrentCube = this.validateCurrentCube.bind(this);
        this.playNextSolutionMove = this.playNextSolutionMove.bind(this);
        this.animateMoveIndex = this.animateMoveIndex.bind(this);
    }

    componentDidMount() {
        this.workspaceElement.addEventListener('mouseup', this.finishPointerAction);
        this.workspaceElement.addEventListener('touchend', this.finishPointerAction);
        this.workspaceElement.addEventListener('touchcancel', this.finishPointerAction);

        window.addEventListener('message', this.handleWindowMessage);
        window.scrambleRubikCube = this.scrambleCube;
        window.getRubikCubeDiscreteState = this.getDiscreteState;
        window.getRubikCubeSolverStatus = this.getSolverStatus;
        window.solveRubikCube = this.solveCube;
        window.pauseRubikCube = this.pauseSolver;
        window.continueRubikCube = this.continueSolver;
        window.stepRubikCube = this.stepSolver;
        window.undoRubikCube = this.undoLastMove;
        window.executeRubikCubeMoves = this.executeMoveSequence;
        window.validateRubikCube = this.validateCurrentCube;

        this.rotateWholeCube(initialViewHorizontalDegrees, initialViewVerticalDegrees);
        this.publishSolverStatus({ metrics: analyzeDiscreteCubeState(this.getDiscreteState()) });
    }

    componentWillUnmount() {
        this.workspaceElement.removeEventListener('mouseup', this.finishPointerAction);
        this.workspaceElement.removeEventListener('touchend', this.finishPointerAction);
        this.workspaceElement.removeEventListener('touchcancel', this.finishPointerAction);
        window.removeEventListener('message', this.handleWindowMessage);

        if (window.scrambleRubikCube === this.scrambleCube) {
            delete window.scrambleRubikCube;
        }

        if (window.getRubikCubeDiscreteState === this.getDiscreteState) {
            delete window.getRubikCubeDiscreteState;
        }

        [
            ['getRubikCubeSolverStatus', this.getSolverStatus],
            ['solveRubikCube', this.solveCube],
            ['pauseRubikCube', this.pauseSolver],
            ['continueRubikCube', this.continueSolver],
            ['stepRubikCube', this.stepSolver],
            ['undoRubikCube', this.undoLastMove],
            ['executeRubikCubeMoves', this.executeMoveSequence],
            ['validateRubikCube', this.validateCurrentCube]
        ].forEach(windowFunction => {
            if (window[windowFunction[0]] === windowFunction[1]) {
                delete window[windowFunction[0]];
            }
        });

        if (this.pendingSnapTimerIdentifier) {
            clearTimeout(this.pendingSnapTimerIdentifier);
        }
        if (this.pendingMoveTimerIdentifier) {
            clearTimeout(this.pendingMoveTimerIdentifier);
        }
    }

    handleWindowMessage(messageEvent) {
        const messageData = messageEvent.data;
        if (messageData === scrambleMessageType ||
            (messageData && messageData.type === scrambleMessageType)) {
            this.scrambleCube();
            return;
        }

        if (!messageData || !messageData.type) return;
        if (messageData.type === 'SOLVE_RUBIK_CUBE') this.solveCube();
        if (messageData.type === 'PAUSE_RUBIK_CUBE') this.pauseSolver();
        if (messageData.type === 'CONTINUE_RUBIK_CUBE') this.continueSolver();
        if (messageData.type === 'STEP_RUBIK_CUBE') this.stepSolver();
        if (messageData.type === 'UNDO_RUBIK_CUBE') this.undoLastMove();
        if (messageData.type === 'VALIDATE_RUBIK_CUBE') this.validateCurrentCube();
        if (messageData.type === 'EXECUTE_RUBIK_CUBE_MOVES') {
            this.executeMoveSequence(messageData.moves || '');
        }
    }

    scrambleCube() {
        if (this.isScrambleRequestActive || this.isProgrammaticMoveActive) {
            return false;
        }

        this.isScrambleRequestActive = true;
        this.cancelSolverQueue();

        if (this.pendingSnapTimerIdentifier) {
            clearTimeout(this.pendingSnapTimerIdentifier);
            this.pendingSnapTimerIdentifier = null;
        }

        // A scramble always begins with exact solved geometry, but it keeps the
        // current whole-cube viewing angle. This prevents repeated scrambles
        // from accumulating floating-point position and orientation errors.
        const currentViewAxis = normalizeRotationAxis(this.state.cubieRotationAxes[0]);
        const currentViewDegrees = isFinite(this.state.cubieRotationDegrees[0]) ?
            this.state.cubieRotationDegrees[0] : 0;

        const scrambledCoordinates = createSolvedCubieCoordinates().map(coordinates =>
            rotatePointAroundAxis(coordinates, currentViewAxis, currentViewDegrees)
        );
        const scrambledRotationAxes = Array(27).fill(0).map(() => currentViewAxis.slice());
        const scrambledRotationDegrees = Array(27).fill(currentViewDegrees);
        const cubeFaceAxes = [
            [0, 0, 1],
            [0, 0, -1],
            [0, 1, 0],
            [0, -1, 0],
            [1, 0, 0],
            [-1, 0, 0]
        ].map(baseFaceAxis => normalizeRotationAxis(rotatePointAroundAxis(
            baseFaceAxis,
            currentViewAxis,
            currentViewDegrees
        )));

        const completedScrambleMoves = [];
        let previousAxisFamily = -1;

        for (let moveIndex = 0; moveIndex < scrambleMoveCount; moveIndex++) {
            let availableFaceIndexes;
            if (previousAxisFamily === 0) {
                availableFaceIndexes = [2, 3, 4, 5];
            } else if (previousAxisFamily === 1) {
                availableFaceIndexes = [0, 1, 4, 5];
            } else if (previousAxisFamily === 2) {
                availableFaceIndexes = [0, 1, 2, 3];
            } else {
                availableFaceIndexes = [0, 1, 2, 3, 4, 5];
            }
            const randomFacePosition = Math.min(
                Math.floor(Math.random() * availableFaceIndexes.length),
                availableFaceIndexes.length - 1
            );
            const selectedFaceIndex = availableFaceIndexes[randomFacePosition];
            const turnChoices = [-1, 1, 2];
            const randomTurnPosition = Math.min(
                Math.floor(Math.random() * turnChoices.length),
                turnChoices.length - 1
            );
            const signedTurnDegrees = turnChoices[randomTurnPosition] * 90;
            const selectedFaceAxis = cubeFaceAxes[selectedFaceIndex];

            for (let cubieIndex = 0; cubieIndex < scrambledCoordinates.length; cubieIndex++) {
                const layerProjection =
                    scrambledCoordinates[cubieIndex][0] * selectedFaceAxis[0] +
                    scrambledCoordinates[cubieIndex][1] * selectedFaceAxis[1] +
                    scrambledCoordinates[cubieIndex][2] * selectedFaceAxis[2];

                if (Math.abs(layerProjection - cubieEdgeLength) < .1) {
                    scrambledCoordinates[cubieIndex] = rotatePointAroundAxis(
                        scrambledCoordinates[cubieIndex],
                        selectedFaceAxis,
                        signedTurnDegrees
                    );

                    const combinedRotation = combineAxisAngleRotations(
                        signedTurnDegrees,
                        selectedFaceAxis,
                        scrambledRotationAxes[cubieIndex],
                        scrambledRotationDegrees[cubieIndex]
                    );
                    scrambledRotationAxes[cubieIndex] = normalizeRotationAxis(
                        combinedRotation.rotationAxis
                    );
                    scrambledRotationDegrees[cubieIndex] = isFinite(combinedRotation.rotationDegrees) ?
                        combinedRotation.rotationDegrees : currentViewDegrees;
                }
            }

            completedScrambleMoves.push({
                faceIndex: selectedFaceIndex,
                degrees: signedTurnDegrees
            });
            previousAxisFamily = Math.floor(selectedFaceIndex / 2);
        }

        this.lastScrambleMoves = completedScrambleMoves;
        this.setState({
            cubieCoordinates: scrambledCoordinates,
            cubieRotationAxes: scrambledRotationAxes,
            cubieRotationDegrees: scrambledRotationDegrees,
            activeLayerDegrees: 0,
            isViewDragActive: false,
            previousPointerPoint: {},
            selectedFaceName: undefined,
            selectedCubieIndex: undefined,
            activeLayerAxisIndex: null,
            isSnapRotationActive: undefined,
            snapStepDegrees: undefined,
            isReverseLayerTurn: false
        }, () => {
            this.isScrambleRequestActive = false;
            this.moveHistory = completedScrambleMoves.map(move =>
                this.moveIndexFromApplicationFace(move.faceIndex, move.degrees)
            );
            this.publishSolverStatus({
                mode: 'scrambled',
                message: '24-move scramble complete. The cube is ready to solve.',
                progressPercent: 0,
                solutionNotation: [],
                currentMoveIndex: 0,
                totalMoves: 0,
                movesRemaining: 0,
                currentMove: ''
            });
        });

        return true;
    }

    getDiscreteState() {
        return getDiscreteCubeState(
            this.state.cubieCoordinates,
            this.state.cubieRotationAxes,
            this.state.cubieRotationDegrees
        );
    }


    getSolverStatus() {
        return Object.assign({}, this.solverStatus, {
            solutionNotation: (this.solverStatus.solutionNotation || []).slice()
        });
    }

    publishSolverStatus(statusPatch) {
        const discrete = this.getDiscreteState();
        const metrics = discrete ? analyzeDiscreteCubeState(discrete) : this.solverStatus.metrics;
        const validation = discrete ? validateDiscreteCubeState(discrete) : this.solverStatus.validation;
        this.solverStatus = Object.assign({}, this.solverStatus, statusPatch || {}, {
            metrics,
            validation
        });

        try {
            if (window.parent && window.parent !== window) {
                window.parent.postMessage({
                    type: solverStatusMessageType,
                    status: this.getSolverStatus()
                }, '*');
            }
        } catch (statusError) {
            // The cube remains fully functional if its host blocks messaging.
        }
        return this.getSolverStatus();
    }

    validateCurrentCube() {
        const discrete = this.getDiscreteState();
        const validation = validateDiscreteCubeState(discrete);
        this.publishSolverStatus({
            mode: validation.isValid ? 'valid' : 'error',
            message: validation.isValid ?
                'Cube state is physically legal and internally consistent.' :
                validation.errors.join(' ')
        });
        return validation;
    }

    solveCube() {
        if (this.isProgrammaticMoveActive || this.state.activeLayerAxisIndex !== null) {
            this.publishSolverStatus({
                mode: 'error',
                message: 'Finish the current cube turn before starting the solver.'
            });
            return false;
        }

        const discrete = this.getDiscreteState();
        const validation = validateDiscreteCubeState(discrete);
        if (!validation.isValid) {
            this.publishSolverStatus({
                mode: 'error',
                message: validation.errors.join(' '),
                validation
            });
            return false;
        }

        this.cancelSolverQueue();
        if (discrete.isSolved) {
            this.publishSolverStatus({
                mode: 'solved',
                message: 'The cube is already solved.',
                progressPercent: 100,
                solutionNotation: [],
                currentMoveIndex: 0,
                totalMoves: 0,
                movesRemaining: 0,
                currentMove: ''
            });
            return true;
        }

        this.publishSolverStatus({
            mode: 'initializing',
            message: 'Building deterministic decision and pruning tables...',
            progressPercent: 1,
            solutionNotation: [],
            currentMoveIndex: 0,
            totalMoves: 0,
            movesRemaining: 0,
            currentMove: '',
            nodesVisited: 0,
            searchMilliseconds: 0
        });

        setTimeout(() => {
            let result;
            try {
                result = solveDiscreteCubeState(discrete, {
                    timeoutMilliseconds: 30000,
                    onInitializeProgress: (message, progressPercent) => {
                        this.publishSolverStatus({
                            mode: 'initializing',
                            message,
                            progressPercent
                        });
                    },
                    onSearchProgress: searchProgress => {
                        this.publishSolverStatus({
                            mode: 'searching',
                            message: `Searching phase ${searchProgress.phase} at depth ${searchProgress.depth}...`,
                            progressPercent: searchProgress.phase === 1 ? 70 : 85,
                            nodesVisited: searchProgress.nodesVisited || 0
                        });
                    }
                });
            } catch (solverError) {
                this.publishSolverStatus({
                    mode: 'error',
                    message: `Solver error: ${solverError.message || solverError}`
                });
                return;
            }

            if (!result.solved) {
                this.publishSolverStatus({
                    mode: 'error',
                    message: result.error || 'The deterministic solver could not find a solution.',
                    nodesVisited: result.nodesVisited || 0,
                    searchMilliseconds: result.milliseconds || 0
                });
                return;
            }

            this.solutionMoves = result.moves.slice();
            this.solutionMoveIndex = 0;
            this.solutionMustEndSolved = true;
            this.isSolverPaused = false;
            this.publishSolverStatus({
                mode: 'playing',
                message: `Solution found: ${result.moves.length} deterministic moves.`,
                progressPercent: 90,
                solutionNotation: result.notation.slice(),
                currentMoveIndex: 0,
                totalMoves: result.moves.length,
                movesRemaining: result.moves.length,
                currentMove: result.notation[0] || '',
                nodesVisited: result.nodesVisited || 0,
                searchMilliseconds: result.milliseconds || 0,
                phaseOneDepth: result.phaseOneDepth || 0,
                phaseTwoDepth: result.phaseTwoDepth || 0
            });
            this.playNextSolutionMove();
        }, 20);

        return true;
    }

    pauseSolver() {
        this.isSolverPaused = true;
        this.publishSolverStatus({
            mode: 'paused',
            message: this.isProgrammaticMoveActive ?
                'Pause requested. The current move will finish safely.' :
                'Automatic solving is paused.'
        });
        return true;
    }

    continueSolver() {
        if (!this.solutionMoves.length || this.solutionMoveIndex >= this.solutionMoves.length) {
            this.publishSolverStatus({
                mode: 'idle',
                message: 'There is no paused solution to continue.'
            });
            return false;
        }
        this.isSolverPaused = false;
        this.publishSolverStatus({
            mode: 'playing',
            message: 'Automatic solving continued.'
        });
        this.playNextSolutionMove();
        return true;
    }

    stepSolver() {
        if (!this.solutionMoves.length || this.solutionMoveIndex >= this.solutionMoves.length) {
            this.publishSolverStatus({
                mode: 'idle',
                message: 'Calculate a solution before requesting the next move.'
            });
            return false;
        }
        if (this.isProgrammaticMoveActive) return false;
        this.isSolverPaused = true;
        this.playNextSolutionMove(true);
        return true;
    }

    playNextSolutionMove(forceSingleStep) {
        if (this.isProgrammaticMoveActive) return false;
        if (this.solutionMoveIndex >= this.solutionMoves.length) {
            const solvedState = this.getDiscreteState();
            if (this.solutionMustEndSolved) {
                this.publishSolverStatus({
                    mode: solvedState && solvedState.isSolved ? 'solved' : 'error',
                    message: solvedState && solvedState.isSolved ?
                        'Solved. Every corner and edge is in its exact legal position.' :
                        'The move sequence ended, but the cube state did not verify as solved.',
                    progressPercent: solvedState && solvedState.isSolved ? 100 : 0,
                    currentMoveIndex: this.solutionMoves.length,
                    movesRemaining: 0,
                    currentMove: ''
                });
            } else {
                this.publishSolverStatus({
                    mode: solvedState && solvedState.isSolved ? 'solved' : 'idle',
                    message: solvedState && solvedState.isSolved ?
                        'Move sequence complete. The cube is solved.' :
                        'Move sequence complete.',
                    progressPercent: solvedState && solvedState.isSolved ? 100 : 0,
                    currentMoveIndex: this.solutionMoves.length,
                    movesRemaining: 0,
                    currentMove: ''
                });
            }
            return true;
        }
        if (this.isSolverPaused && !forceSingleStep) return false;

        const moveIndex = this.solutionMoves[this.solutionMoveIndex];
        const moveNumber = this.solutionMoveIndex + 1;
        this.publishSolverStatus({
            mode: forceSingleStep ? 'paused' : 'playing',
            message: `Executing move ${moveNumber} of ${this.solutionMoves.length}: ${notationForMoveIndex(moveIndex)}`,
            currentMoveIndex: this.solutionMoveIndex,
            movesRemaining: this.solutionMoves.length - this.solutionMoveIndex,
            currentMove: notationForMoveIndex(moveIndex),
            progressPercent: 90 + Math.round((this.solutionMoveIndex / this.solutionMoves.length) * 10)
        });

        this.animateMoveIndex(moveIndex, true, () => {
            this.solutionMoveIndex++;
            const remaining = this.solutionMoves.length - this.solutionMoveIndex;
            this.publishSolverStatus({
                currentMoveIndex: this.solutionMoveIndex,
                movesRemaining: remaining,
                currentMove: remaining ? notationForMoveIndex(this.solutionMoves[this.solutionMoveIndex]) : ''
            });

            if (this.solutionMoveIndex >= this.solutionMoves.length) {
                this.playNextSolutionMove(false);
            } else if (forceSingleStep || this.isSolverPaused) {
                this.publishSolverStatus({
                    mode: 'paused',
                    message: `Paused with ${remaining} move${remaining === 1 ? '' : 's'} remaining.`
                });
            } else {
                this.pendingMoveTimerIdentifier = setTimeout(this.playNextSolutionMove, 35);
            }
        });
        return true;
    }

    undoLastMove() {
        if (this.isProgrammaticMoveActive || !this.moveHistory.length) {
            this.publishSolverStatus({
                mode: 'idle',
                message: this.isProgrammaticMoveActive ?
                    'The current move must finish before undo.' :
                    'There is no move to undo.'
            });
            return false;
        }

        this.cancelSolverQueue();
        const previousMove = this.moveHistory.pop();
        const inverseMove = invertMoveIndex(previousMove);
        this.publishSolverStatus({
            mode: 'playing',
            message: `Undoing ${notationForMoveIndex(previousMove)} with ${notationForMoveIndex(inverseMove)}.`
        });
        this.animateMoveIndex(inverseMove, false, () => {
            this.publishSolverStatus({
                mode: 'idle',
                message: 'Last move undone.'
            });
        });
        return true;
    }

    executeMoveSequence(sequence) {
        if (this.isProgrammaticMoveActive) return false;
        const moves = parseMoveSequence(sequence);
        if (!moves.length || moves.some(move => move === null)) {
            this.publishSolverStatus({
                mode: 'error',
                message: "Move sequence is invalid. Use notation such as R U R' U' F2."
            });
            return false;
        }

        this.cancelSolverQueue();
        this.solutionMoves = moves.slice();
        this.solutionMoveIndex = 0;
        this.solutionMustEndSolved = false;
        this.isSolverPaused = false;
        this.publishSolverStatus({
            mode: 'playing',
            message: `Executing ${moves.length} supplied move${moves.length === 1 ? '' : 's'}.`,
            solutionNotation: moves.map(notationForMoveIndex),
            currentMoveIndex: 0,
            totalMoves: moves.length,
            movesRemaining: moves.length,
            currentMove: notationForMoveIndex(moves[0])
        });
        this.playNextSolutionMove();
        return true;
    }

    cancelSolverQueue() {
        this.solutionMoves = [];
        this.solutionMoveIndex = 0;
        this.solutionMustEndSolved = false;
        this.isSolverPaused = false;
        if (this.pendingMoveTimerIdentifier) {
            clearTimeout(this.pendingMoveTimerIdentifier);
            this.pendingMoveTimerIdentifier = null;
        }
    }

    moveIndexFromApplicationFace(faceIndex, degrees) {
        const faceName = applicationFaceNameByAxisIndex[faceIndex];
        const solverFaceIndex = ['U', 'D', 'F', 'B', 'R', 'L'].indexOf(faceName);
        const normalized = ((Math.round(degrees / 90) % 4) + 4) % 4;
        if (normalized === 0 || solverFaceIndex < 0) return null;
        const powerIndex = normalized === 1 ? 0 : (normalized === 2 ? 1 : 2);
        return solverFaceIndex * 3 + powerIndex;
    }

    recordManualLayerTurn(axisIndex, degrees) {
        if (axisIndex === null || !degrees) return;
        const moveIndex = this.moveIndexFromApplicationFace(axisIndex, degrees);
        if (moveIndex === null) return;
        this.moveHistory.push(moveIndex);
        this.cancelSolverQueue();
        this.publishSolverStatus({
            mode: 'idle',
            message: `Manual move recorded: ${notationForMoveIndex(moveIndex)}.`
        });
    }

    animateMoveIndex(moveIndex, recordHistory, completionCallback) {
        if (this.isProgrammaticMoveActive) return false;
        const discreteBeforeMove = this.getDiscreteState();
        if (!validateDiscreteCubeState(discreteBeforeMove).isValid) return false;

        const faceIndex = Math.floor(moveIndex / 3);
        const faceName = ['U', 'D', 'F', 'B', 'R', 'L'][faceIndex];
        const powerIndex = moveIndex % 3;
        const totalDegrees = powerIndex === 0 ? 90 : (powerIndex === 1 ? 180 : -90);
        let appliedDegrees = 0;
        this.isProgrammaticMoveActive = true;

        const applyNextStep = () => {
            const remainingDegrees = totalDegrees - appliedDegrees;
            if (Math.abs(remainingDegrees) < .001) {
                this.isProgrammaticMoveActive = false;
                if (recordHistory) this.moveHistory.push(moveIndex);
                if (completionCallback) completionCallback();
                return;
            }

            const signedStep = remainingDegrees > 0 ?
                Math.min(moveAnimationStepDegrees, remainingDegrees) :
                -Math.min(moveAnimationStepDegrees, Math.abs(remainingDegrees));
            this.applyProgrammaticLayerRotation(faceName, signedStep, () => {
                appliedDegrees += signedStep;
                this.pendingMoveTimerIdentifier = setTimeout(
                    applyNextStep,
                    moveAnimationDelayMilliseconds
                );
            });
        };

        applyNextStep();
        return true;
    }

    applyProgrammaticLayerRotation(faceName, turnDegrees, completionCallback) {
        const viewAxis = normalizeRotationAxis(this.state.cubieRotationAxes[0]);
        const viewDegrees = this.state.cubieRotationDegrees[0] || 0;
        const worldFaceAxis = normalizeRotationAxis(rotatePointAroundAxis(
            bodyFaceAxisByName[faceName],
            viewAxis,
            viewDegrees
        ));
        const nextCoordinates = this.state.cubieCoordinates.slice();
        const nextAxes = this.state.cubieRotationAxes.slice();
        const nextDegrees = this.state.cubieRotationDegrees.slice();

        for (let cubieIndex = 0; cubieIndex < nextCoordinates.length; cubieIndex++) {
            const projection =
                nextCoordinates[cubieIndex][0] * worldFaceAxis[0] +
                nextCoordinates[cubieIndex][1] * worldFaceAxis[1] +
                nextCoordinates[cubieIndex][2] * worldFaceAxis[2];

            if (Math.abs(projection - cubieEdgeLength) < .1) {
                nextCoordinates[cubieIndex] = rotatePointAroundAxis(
                    nextCoordinates[cubieIndex],
                    worldFaceAxis,
                    turnDegrees
                );
                const combined = combineAxisAngleRotations(
                    turnDegrees,
                    worldFaceAxis,
                    nextAxes[cubieIndex],
                    nextDegrees[cubieIndex]
                );
                nextAxes[cubieIndex] = normalizeRotationAxis(combined.rotationAxis);
                nextDegrees[cubieIndex] = combined.rotationDegrees;
            }
        }

        this.setState({
            cubieCoordinates: nextCoordinates,
            cubieRotationAxes: nextAxes,
            cubieRotationDegrees: nextDegrees,
            activeLayerDegrees: 0,
            activeLayerAxisIndex: null,
            isSnapRotationActive: undefined
        }, completionCallback);
    }

    readCubieOrientation(cubieIndex) {
        return [
            this.state.cubieRotationAxes[cubieIndex][0],
            this.state.cubieRotationAxes[cubieIndex][1],
            this.state.cubieRotationAxes[cubieIndex][2],
            this.state.cubieRotationDegrees[cubieIndex]
        ];
    }

    beginViewDrag(pointerEvent) {
        if (this.isProgrammaticMoveActive) return;
        const pointerCoordinates = readPointerCoordinates(pointerEvent);
        this.setState({
            isViewDragActive: true,
            previousPointerPoint: { x: pointerCoordinates.clientX, y: pointerCoordinates.clientY }
        });
    }

    rotateWholeCube(horizontalDelta, verticalDelta) {
        const nextCubieCoordinates = this.state.cubieCoordinates.slice();
        const nextRotationDegrees = [];
        const nextRotationAxes = [];

        for (let cubieIndex = 0; cubieIndex < nextCubieCoordinates.length; cubieIndex++) {
            nextCubieCoordinates[cubieIndex] = Math.abs(verticalDelta) > Math.abs(horizontalDelta) ?
                rotatePointAroundAxis(this.state.cubieCoordinates[cubieIndex], [1, 0, 0], -verticalDelta) :
                rotatePointAroundAxis(this.state.cubieCoordinates[cubieIndex], [0, 1, 0], horizontalDelta);

            const combinedRotation = Math.abs(verticalDelta) > Math.abs(horizontalDelta) ?
                combineAxisAngleRotations(
                    -verticalDelta,
                    [1, 0, 0],
                    this.state.cubieRotationAxes[cubieIndex],
                    this.state.cubieRotationDegrees[cubieIndex]
                ) :
                combineAxisAngleRotations(
                    horizontalDelta,
                    [0, 1, 0],
                    this.state.cubieRotationAxes[cubieIndex],
                    this.state.cubieRotationDegrees[cubieIndex]
                );

            nextRotationDegrees[cubieIndex] = combinedRotation.rotationDegrees;
            nextRotationAxes[cubieIndex] = combinedRotation.rotationAxis;
        }

        this.setState({
            cubieCoordinates: nextCubieCoordinates,
            cubieRotationDegrees: nextRotationDegrees,
            cubieRotationAxes: nextRotationAxes
        });
    }

    handlePointerMove(pointerEvent) {
        if (this.isProgrammaticMoveActive) return;
        const pointerCoordinates = readPointerCoordinates(pointerEvent);

        if (this.state.isViewDragActive) {
            const verticalDelta = pointerCoordinates.clientY - this.state.previousPointerPoint.y;
            const horizontalDelta = pointerCoordinates.clientX - this.state.previousPointerPoint.x;

            this.setState({
                previousPointerPoint: { x: pointerCoordinates.clientX, y: pointerCoordinates.clientY }
            }, () => {
                this.rotateWholeCube(horizontalDelta, verticalDelta);
            });
        } else if (this.state.selectedFaceName) {
            const verticalDelta = pointerCoordinates.clientY - this.state.previousPointerPoint.y;
            const horizontalDelta = pointerCoordinates.clientX - this.state.previousPointerPoint.x;

            this.setState({
                previousPointerPoint: { x: pointerCoordinates.clientX, y: pointerCoordinates.clientY }
            });

            this.rotateSelectedLayer(
                horizontalDelta / 2,
                verticalDelta / 2,
                this.state.cubieCoordinates[this.state.selectedCubieIndex],
                this.state.selectedFaceName,
                this.readCubieOrientation(this.state.selectedCubieIndex)
            );
        }
    }

    finishPointerAction() {
        this.setState({
            isViewDragActive: false,
            previousPointerPoint: {},
            selectedFaceName: undefined
        });

        if (this.state.activeLayerAxisIndex !== null) {
            this.snapLayerToQuarterTurn();
        }

        if (this.legacyRotationFlag === 1) {
            this.rotateWholeCube(2, 220);
        }
    }

    snapLayerToQuarterTurn() {
        if (this.state.activeLayerDegrees % 90 === 0) {
            if (!this.isProgrammaticMoveActive &&
                this.state.activeLayerAxisIndex !== null &&
                this.state.activeLayerDegrees !== 0) {
                this.recordManualLayerTurn(
                    this.state.activeLayerAxisIndex,
                    this.state.activeLayerDegrees
                );
            }
            this.setState({
                activeLayerDegrees: 0,
                activeLayerAxisIndex: null,
                isSnapRotationActive: undefined
            });
            return;
        }

        const snapStepDegrees =
            Math.abs(this.state.activeLayerDegrees % 90) < 80 &&
            Math.abs(this.state.activeLayerDegrees % 90) > 10 ? 3 : 1;

        this.setState({
            isSnapRotationActive: true,
            snapStepDegrees: snapStepDegrees,
            isReverseLayerTurn: (
                !this.state.isSnapRotationActive &&
                Math.abs(this.state.activeLayerDegrees % 90) < 30
            ) ? !this.state.isReverseLayerTurn : this.state.isReverseLayerTurn
        }, () => {
            this.rotateSelectedLayer(Math.sqrt(.5), Math.sqrt(.5), null);
            this.pendingSnapTimerIdentifier = setTimeout(this.snapLayerToQuarterTurn, .001);
        });
    }

    rotateSelectedLayer(horizontalMovement, verticalMovement, selectedCubieCoordinates, selectedFaceName, selectedCubieOrientation) {
        if (this.state.isSnapRotationActive && selectedFaceName) {
            return;
        }

        if (horizontalMovement === 0 && verticalMovement === 0) {
            return;
        }

        const turnStepDegrees = selectedFaceName ?
            Math.round(Math.sqrt(horizontalMovement * horizontalMovement + verticalMovement * verticalMovement)) :
            this.state.snapStepDegrees;

        const updatedRotationAxes = this.state.cubieRotationAxes.slice();
        const updatedRotationDegrees = this.state.cubieRotationDegrees.slice();
        const nextCubieCoordinates = this.state.cubieCoordinates.slice();

        const rotatedFaceAxes = [
            [0, 0, 1],
            [0, 0, -1],
            [0, 1, 0],
            [0, -1, 0],
            [1, 0, 0],
            [-1, 0, 0]
        ];

        rotatedFaceAxes.forEach((baseFaceAxis, faceAxisIndex) => {
            rotatedFaceAxes[faceAxisIndex] = rotatePointAroundAxis(
                baseFaceAxis,
                updatedRotationAxes[0],
                updatedRotationDegrees[0]
            );
        });

        let chosenAxisIndex = 0;
        let shouldReverseTurn = false;

        if (selectedFaceName) {
            let candidateCoordinates;
            let smallestProjectionGap = 1000;

            if (this.state.activeLayerDegrees) {
                chosenAxisIndex = this.state.activeLayerAxisIndex;
                candidateCoordinates = rotatePointAroundAxis(
                    selectedCubieCoordinates.slice(),
                    rotatedFaceAxes[chosenAxisIndex].slice(),
                    turnStepDegrees
                );

                if (smallestProjectionGap > measureProjectedMoveGap(
                    candidateCoordinates,
                    selectedCubieCoordinates,
                    horizontalMovement,
                    verticalMovement
                )) {
                    smallestProjectionGap = measureProjectedMoveGap(
                        candidateCoordinates,
                        selectedCubieCoordinates,
                        horizontalMovement,
                        verticalMovement
                    );
                    shouldReverseTurn = false;
                }

                candidateCoordinates = rotatePointAroundAxis(
                    selectedCubieCoordinates.slice(),
                    rotatedFaceAxes[chosenAxisIndex].slice(),
                    -turnStepDegrees
                );

                if (smallestProjectionGap > measureProjectedMoveGap(
                    candidateCoordinates,
                    selectedCubieCoordinates,
                    horizontalMovement,
                    verticalMovement
                )) {
                    smallestProjectionGap = measureProjectedMoveGap(
                        candidateCoordinates,
                        selectedCubieCoordinates,
                        horizontalMovement,
                        verticalMovement
                    );
                    shouldReverseTurn = true;
                }
            } else {
                const touchedFaceVector = rotatePointAroundAxis(
                    faceOffsetByName[selectedFaceName],
                    [selectedCubieOrientation[0], selectedCubieOrientation[1], selectedCubieOrientation[2]],
                    selectedCubieOrientation[3]
                );

                for (let candidateAxisIndex = 0; candidateAxisIndex < rotatedFaceAxes.length; candidateAxisIndex++) {
                    const selectedCubieAxisProjection =
                        selectedCubieCoordinates[0] * rotatedFaceAxes[candidateAxisIndex][0] +
                        selectedCubieCoordinates[1] * rotatedFaceAxes[candidateAxisIndex][1] +
                        selectedCubieCoordinates[2] * rotatedFaceAxes[candidateAxisIndex][2];

                    const touchedFaceAxisProjection =
                        touchedFaceVector[0] * rotatedFaceAxes[candidateAxisIndex][0] +
                        touchedFaceVector[1] * rotatedFaceAxes[candidateAxisIndex][1] +
                        touchedFaceVector[2] * rotatedFaceAxes[candidateAxisIndex][2];

                    if (Math.abs(selectedCubieAxisProjection - cubieEdgeLength) < .1 &&
                        Math.abs(touchedFaceAxisProjection - cubieEdgeLength) > .1) {
                        candidateCoordinates = rotatePointAroundAxis(
                            selectedCubieCoordinates.slice(),
                            rotatedFaceAxes[candidateAxisIndex].slice(),
                            turnStepDegrees
                        );

                        if (smallestProjectionGap > measureProjectedMoveGap(
                            candidateCoordinates,
                            selectedCubieCoordinates,
                            horizontalMovement,
                            verticalMovement
                        )) {
                            smallestProjectionGap = measureProjectedMoveGap(
                                candidateCoordinates,
                                selectedCubieCoordinates,
                                horizontalMovement,
                                verticalMovement
                            );
                            chosenAxisIndex = candidateAxisIndex;
                            shouldReverseTurn = false;
                        }

                        candidateCoordinates = rotatePointAroundAxis(
                            selectedCubieCoordinates.slice(),
                            rotatedFaceAxes[candidateAxisIndex].slice(),
                            -turnStepDegrees
                        );

                        if (smallestProjectionGap > measureProjectedMoveGap(
                            candidateCoordinates,
                            selectedCubieCoordinates,
                            horizontalMovement,
                            verticalMovement
                        )) {
                            smallestProjectionGap = measureProjectedMoveGap(
                                candidateCoordinates,
                                selectedCubieCoordinates,
                                horizontalMovement,
                                verticalMovement
                            );
                            chosenAxisIndex = candidateAxisIndex;
                            shouldReverseTurn = true;
                        }
                    }
                }
            }

            this.setState({
                activeLayerAxisIndex: chosenAxisIndex,
                isReverseLayerTurn: shouldReverseTurn
            });
        } else {
            shouldReverseTurn = this.state.isReverseLayerTurn;
            chosenAxisIndex = this.state.activeLayerAxisIndex;
        }

        for (let movingCubieIndex = 0; movingCubieIndex < nextCubieCoordinates.length; movingCubieIndex++) {
            const axisDotX = nextCubieCoordinates[movingCubieIndex][0] * rotatedFaceAxes[chosenAxisIndex][0];
            const axisDotY = nextCubieCoordinates[movingCubieIndex][1] * rotatedFaceAxes[chosenAxisIndex][1];
            const axisDotZ = nextCubieCoordinates[movingCubieIndex][2] * rotatedFaceAxes[chosenAxisIndex][2];

            if (Math.abs((axisDotX + axisDotY + axisDotZ) - cubieEdgeLength) < .1) {
                const signedTurnDegrees = shouldReverseTurn ? -turnStepDegrees : turnStepDegrees;
                nextCubieCoordinates[movingCubieIndex] = rotatePointAroundAxis(
                    this.state.cubieCoordinates[movingCubieIndex],
                    rotatedFaceAxes[chosenAxisIndex],
                    signedTurnDegrees
                );

                const combinedRotation = combineAxisAngleRotations(
                    signedTurnDegrees,
                    rotatedFaceAxes[chosenAxisIndex],
                    updatedRotationAxes[movingCubieIndex].slice(),
                    updatedRotationDegrees[movingCubieIndex]
                );

                updatedRotationAxes[movingCubieIndex] = combinedRotation.rotationAxis;
                updatedRotationDegrees[movingCubieIndex] = combinedRotation.rotationDegrees;
            }
        }

        this.setState({
            cubieCoordinates: nextCubieCoordinates,
            cubieRotationDegrees: updatedRotationDegrees,
            cubieRotationAxes: updatedRotationAxes,
            activeLayerDegrees: this.state.activeLayerDegrees +
                (shouldReverseTurn ? -turnStepDegrees : turnStepDegrees)
        });
    }

    calculateViewportScale() {
        const limitingViewportDimension = window.innerHeight > window.innerWidth ?
            window.innerWidth : window.innerHeight;
        return Math.min(Math.max(limitingViewportDimension / 300, 1), 1.5);
    }

    beginLayerTurn(pointerOrigin, faceName, cubieIndex) {
        if (this.isProgrammaticMoveActive ||
            (this.solutionMoves.length && !this.isSolverPaused)) {
            return;
        }
        this.setState({
            selectedFaceName: faceName,
            previousPointerPoint: pointerOrigin,
            selectedCubieIndex: cubieIndex
        });
    }

    render() {
        return (
            <div ref={workspaceNode => this.workspaceElement = workspaceNode}
                className="cube-container"
                style={{ transform: `scale(${this.calculateViewportScale()})` }}
                onMouseDown={this.beginViewDrag}
                onTouchStart={this.beginViewDrag}
                onMouseMove={this.handlePointerMove}
                onTouchMove={this.handlePointerMove}>
                {this.state.cubieCoordinates.map((cubieCoordinates, cubieIndex) => {
                    return (
                        <RubikCubie key={cubieIndex}
                            beginLayerTurn={(pointerOrigin, faceName) => {
                                this.beginLayerTurn(pointerOrigin, faceName, cubieIndex);
                            }}
                            positionOffset={cubieCoordinates}
                            axisAngleOrientation={this.readCubieOrientation(cubieIndex)} />
                    );
                })}
            </div>
        );
    }
}

export default RubikCubeWorkspace;
